// StakeholderRelationRadar.jsx - FINAL (layout applied, logic untouched)
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import pandaAvatar from "../../../../../../assets/PandaProfile.png";
import { progressService } from "../../../../../../services/progressService";

import Lion from "../../../../../../assets/animals/Lion.png";
import Monkey from "../../../../../../assets/animals/Monkey.png";
import Tiger from "../../../../../../assets/animals/Tiger.png";
import Koala from "../../../../../../assets/animals/Koala.png";
import Fox from "../../../../../../assets/animals/Fox.png";
import Panda from "../../../../../../assets/animals/Piglet.png";
import Cat from "../../../../../../assets/animals/Cat.png";
import Gorilla from "../../../../../../assets/animals/Gorilla.png";
import Hyena from "../../../../../../assets/animals/Hyena.png";

const animalIcons = [Lion, Monkey, Tiger, Koala, Fox, Panda, Cat, Gorilla, Hyena];

const getAnimalImage = (stakeholder, fallbackIndex = 0) => {
  if (stakeholder.animal && typeof stakeholder.animal === "string") {
    return stakeholder.animal;
  }
  return animalIcons[fallbackIndex % animalIcons.length];
};

const StakeholderRelationRadar = ({ userId, subsectionId, onNextStep }) => {
  const [visibilityMap, setVisibilityMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showContinue, setShowContinue] = useState(false);
  const [animationReady, setAnimationReady] = useState(false);
  const [userProfileImage, setUserProfileImage] = useState(null);
  const [selectedBoss, setSelectedBoss] = useState(null);

  useEffect(() => {
    async function loadUserProfile() {
      try {
        const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
        const profileProgress = await progressService.getUserProgress(userId, CURRENT_IDENTITY_SUBSECTION_ID);
        const payload = profileProgress?.data?.payload;
        if (payload?.profileImage) {
          setUserProfileImage(payload.profileImage);
        }
      } catch (err) {
        console.error("Failed to load user profile:", err);
      }
    }
    if (userId) loadUserProfile();
  }, [userId]);

  useEffect(() => {
    async function loadVisibilityMap() {
      setLoading(true);
      try {
        if (subsectionId) {
          const progressRes = await progressService.getUserProgress(userId, subsectionId);
          const progressPayload = progressRes?.data?.payload || progressRes?.payload || {};
          const selectedStakeholders = progressPayload?.selectedStakeholders || [];
          const bossStakeholder = selectedStakeholders[0];
          if (bossStakeholder) {
            setSelectedBoss(bossStakeholder);
          }
        }

        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "interpersonal",
          "interpersonal_map"
        );
        if (res?.visibilityMap) {
          setVisibilityMap(res.visibilityMap);
        }
      } catch (e) {
        console.error("Error loading visibility map:", e);
      }
      setLoading(false);
    }
    if (userId) loadVisibilityMap();
  }, [userId, subsectionId]);

  useEffect(() => {
    if (!loading && Object.keys(visibilityMap).length > 0 && !animationReady) {
      const timer = setTimeout(() => setAnimationReady(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [loading, visibilityMap, animationReady]);

  useEffect(() => {
    if (animationReady) {
      const timer = setTimeout(() => {
        setAnimationComplete(true);
        setTimeout(() => setShowContinue(true), 500);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [animationReady]);

  const GAP_BETWEEN_CIRCLES = 280;
  const STARTING_RADIUS = 300;
  const circleRings = Array.from({ length: 4 }, (_, i) => STARTING_RADIUS + i * GAP_BETWEEN_CIRCLES);

  const getStakeholders = (key) => visibilityMap[key]?.items || [];

  const polarToCartesian = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
  };

  const allStakeholders = [
    ...getStakeholders("q1"),
    ...getStakeholders("q2"),
    ...getStakeholders("q3"),
    ...getStakeholders("q4"),
  ];

  const bossStakeholder = selectedBoss
    ? allStakeholders.find((s) => s.id === selectedBoss.id)
    : allStakeholders.find((s) => (s.relation || "").toLowerCase().includes("boss"));

  if (!bossStakeholder && selectedBoss) {
    console.warn("Selected boss not found in visibility map:", selectedBoss);
  } else if (bossStakeholder) {
    console.log("Boss for animation:", bossStakeholder.name, "ID:", bossStakeholder.id);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <p className="text-gray-500 text-lg">Loading radar...</p>
      </div>
    );
  }

  if (Object.keys(visibilityMap).length === 0 || allStakeholders.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">No stakeholder data found</p>
          <p className="text-gray-500 mb-4">Please complete the Interpersonal Map first</p>
          <button
            onClick={onNextStep}
            className="flex items-center justify-center px-8 py-3 bg-[#5EB1BF] hover:bg-[#4CA0AC] text-white font-semibold rounded-full shadow-lg transition"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  const CENTER_X = 150;   // sun pushed left
  const CENTER_Y = 420;

  const quadrantToOrbit = {
    q3: 0,
    q4: 1,
    q2: 2,
    q1: 3,
  };

  const quadrantSettings = {
    q3: { angle: 30, leftShift: -70 },
    q4: { angle: 350, leftShift: -70 },
    q2: { angle: 350, leftShift: -70 },
    q1: { angle: 358, leftShift: -40 },
  };

  const initialOrbit = 3;
  const finalOrbit = 2;
  const initialRadius = circleRings[initialOrbit];
  const finalRadius = circleRings[finalOrbit];

  const initialPos = polarToCartesian(initialRadius, 2);
  const finalPos = polarToCartesian(finalRadius, 2);

  // const stakeholderVariants = {
  //   hidden: { opacity: 0, scale: 0.85 },
  //   visible: (i) => ({
  //     opacity: 1,
  //     scale: 1,
  //     transition: { delay: i * 0.08, duration: 0.35 },
  //   }),
  // };

  const LAST_ORBIT_INDEX = circleRings.length - 1;

  return (
    <>
      <motion.div
        className="relative w-full h-[90vh] bg-white overflow-hidden"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center z-50 w-[1300px]">
          <motion.h3
            className="font-[Gotham] font-medium text-[28px] leading-[135%] text-center text-[#323232]"
            initial={{ opacity: 1 }}
            animate={{ opacity: animationComplete ? 0 : 1 }}
            transition={{ duration: 0.5 }}
          >
            When you started, your visibility chances with
            your boss were very low.
          </motion.h3>
          <motion.p
            className="font-[Gotham] font-medium text-[28px] leading-[135%] absolute top-0 left-0 w-full"
            style={{ color: "#323232" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: animationComplete ? 1 : 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            With this homework, you're now 30% closer to getting visibility from your boss!
          </motion.p>
        </div>

        <div className="absolute left-0 right-0" style={{ overflowY: "hidden", overflowX: "hidden" }}>
          <div
            className="relative mx-auto"
            style={{
              width: "1440px",
              height: "1080px",
              minWidth: "1440px",
              minHeight: "1080px",
            }}
          >
            {circleRings.map((r) => (
              <div
                key={r}
                className="absolute rounded-full border border-[#5EB1BF]"
                style={{ width: `${r * 2}px`, height: `${r * 2}px`, left: `150px`, top: `420px`, transform: `translate(-${r}px, -${r}px)` }}
              />
            ))}

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute z-30"
              style={{
                left: `${CENTER_X - 70}px`,
                top: `${CENTER_Y - 85 }px`,
                transform: "translate(-50%, -50%)",
              }}
                           
            >
              <div className="w-[176.55px] h-[176.55px] rounded-full bg-[#E7E7E7] shadow-md overflow-hidden flex-shrink-0">
                <img
                  src={userProfileImage || pandaAvatar}
                  alt="You"
                  className="w-[140.95px] h-[140.95px] object-cover absolute inset-0 m-auto"
                  style={{ borderRadius: "70.47px", opacity: 1 }}
                />
              </div>
            </motion.div>


                {/* ✅ FIXED: Line shrinks from right, left anchor stays fixed */}
{bossStakeholder && (
  <motion.div
    className="absolute z-30"
    style={{
      left: `${CENTER_X + 150}px`,  // ✅ Fixed left position (no centering)
      top: `${CENTER_Y + 10}px`,
      transform: "translateY(-50%)",  // ✅ Only center vertically
    }}
  >
    <motion.div
      className="relative flex items-center"
      style={{
        width: animationReady ? "600px" : "920px",
        transition: "width 1s cubic-bezier(.4,0,.2,1)",
      }}
    >
      <div className="absolute left-0 w-3 h-3 rounded-full bg-[#B0B0B0] -translate-x-1/2" />
      <div className="w-full h-[2px] bg-[#B0B0B0]" />
      <div className="absolute right-0 w-3 h-3 rounded-full bg-[#B0B0B0] translate-x-1/2" />
    </motion.div>

    <div className="pt-2 w-full flex justify-center">
      <span className="whitespace-nowrap font-[Gotham] font-medium text-[14px] leading-[135%] uppercase text-black">
        {animationComplete ? (
          <>
            CHANCES OF VISIBILITY INCREASED FROM &lt;10% to {"→"}{" "}
            <span className="text-[#5EB1BF]">30%</span>
          </>
        ) : (
          <>CHANCES OF VISIBILITY &lt;10%</>
        )}
      </span>
    </div>
  </motion.div>
)}



            {(() => {
              let stakeholderIndex = 0;
              return ["q3", "q4", "q2", "q1"].flatMap((qKey) => {
                const stakeholders = getStakeholders(qKey);
                if (stakeholders.length === 0) return [];

                const filtered = stakeholders.filter((s) => {
                  if (bossStakeholder && s.id === bossStakeholder.id) {
                    return false;
                  }
                  return true;
                });

                if (filtered.length === 0) return [];

                const orbitIndex = quadrantToOrbit[qKey];
                const r = circleRings[orbitIndex];
                const settings = quadrantSettings[qKey];
                const n = filtered.length;

                return filtered.map((s, idx) => {
                  const isLastOrbit = orbitIndex === LAST_ORBIT_INDEX;
                  const borderTailwind = isLastOrbit ? "border-2 border-[#FF5722]" : "border-[3.48px] border-[#7C7D7E]";
                  const textTailwind = isLastOrbit ? "text-gray-900" : "text-[#7C7D7E]";
                  const itemOpacity = isLastOrbit ? "opacity-100" : "opacity-50";

                  let angle;
                  if (n === 1) {
                    angle = settings.angle;
                  } else {
                    const spread = isLastOrbit ? 30 : 40;
                    const angleStep = spread / (n + 1);
                    angle = settings.angle - spread / 2 + angleStep * (idx + 1);
                  }

                  const { x, y } = polarToCartesian(r, angle);
                  const leftShift = settings.leftShift;
                  const currentIndex = stakeholderIndex++;

                  // ✅ FIXED: Different timing for last orbit vs others
                  let shouldShow, animationDelay;
                  
                  if (isLastOrbit) {
                    // Last orbit: Show ONLY after boss animation completes (1.2s)
                    shouldShow = animationReady;
                    animationDelay = 1.2 + (idx * 0.08);  // Start at 1.2s, stagger each item
                  } else {
                    // Other orbits: Show immediately, no delay
                    shouldShow = true;
                    animationDelay = currentIndex * 0.08;
                  }

                  return (
                    <motion.div
                      key={`${qKey}-${s.id}`}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ 
                        opacity: shouldShow ? 1 : 0,  // ✅ Control visibility
                        scale: shouldShow ? 1 : 0.85,
                        transition: { 
                          delay: animationDelay,
                          duration: 0.35 
                        }
                      }}
                      className={`absolute flex flex-col items-center text-center z-20 ${itemOpacity}`}
                      style={{
                        left: `${CENTER_X + x + leftShift}px`,
                        top: `${CENTER_Y - y}px`,
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <div className={`w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-md ${borderTailwind}`}>
                        <img src={getAnimalImage(s, currentIndex)} alt={s.name} className="w-10 h-10 object-contain" />
                      </div>
                      <p className={`mt-2 text-sm font-medium whitespace-nowrap font-[Gotham] ${textTailwind}`}>{s.name}</p>
                      <p className={`text-xs whitespace-nowrap font-[Gotham] ${textTailwind}`}>{s.relation}</p>
                    </motion.div>
                  );
                });
              });
            })()}
             


            {bossStakeholder && (
              <motion.div
                initial={{
                  x: initialPos.x + quadrantSettings.q1.leftShift,
                  y: -initialPos.y,
                  opacity: 0,
                  scale: 0.85,
                }}
                animate={{
                  x: animationReady ? finalPos.x + quadrantSettings.q1.leftShift - 20 : initialPos.x + quadrantSettings.q1.leftShift,
                  y: animationReady ? -finalPos.y : -initialPos.y,
                  opacity: 1,
                  scale: 1,
                }}
                transition={{
                  x: { delay: 0, duration: 1, ease: "easeInOut" },
                  y: { delay: 0, duration: 1, ease: "easeInOut" },
                  opacity: { duration: 0.35 },
                  scale: { duration: 0.35 },
                }}
                className="absolute flex flex-col items-center text-center z-25"
                style={{
                  left: `${CENTER_X}px`,
                  top: `${CENTER_Y}px`,
                  transform: "translate(-50%, -50%)",
                }}
                
                
              >
                <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] flex items-center justify-center shadow-md">
                  <img src={getAnimalImage(bossStakeholder, 0)} alt={bossStakeholder.name} className="w-10 h-10 object-contain" />
                </div>
                <p className="mt-2 text-sm font-medium text-gray-700 whitespace-nowrap">{bossStakeholder.name}</p>
                <p className="text-xs text-gray-500 whitespace-nowrap">{bossStakeholder.relation}</p>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {showContinue && (
        <motion.div className="fixed bottom-4 left-1/2 z-50" style={{ left: "calc(50% - 50px)", transform: "translateX(-50%)" }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <button onClick={onNextStep} className="flex items-center justify-center px-8 py-3 bg-[#54B0AF] hover:bg-teal-600 text-white font-medium font-gotham rounded-full shadow-lg transition">
            Continue
          </button>
        </motion.div>
      )}
    </>
  );
};

export default StakeholderRelationRadar;